/**
 * SessionTracker - Frontend session tracking utility
 * Manages session lifecycle including session start, visitor ID, and session end detection
 */
export class SessionTracker {
  constructor() {
    this.sessionId = null;
    this.visitorId = null;
    this.sessionStart = null;
    this.sessionEnd = null;
    this.isInitialized = false;
    this.sessionTimeout = 30 * 60 * 1000; // 30 minutes in milliseconds
    this.heartbeatInterval = null;
    this.lastActivityTime = null;
    
    this.init();
  }

  /**
   * Initialize session tracking
   */
  init() {
    if (this.isInitialized) return;
    
    console.log('🎯 SessionTracker: Initializing session tracking...');
    
    // Load or create visitor ID (persistent across sessions)
    this.visitorId = this.getOrCreateVisitorId();
    
    // Load or create session data
    this.loadOrCreateSession();
    
    // Set up session end detection
    this.setupSessionEndDetection();
    
    // Set up activity tracking
    this.setupActivityTracking();
    
    // Set up heartbeat to detect inactivity
    this.startHeartbeat();
    
    this.isInitialized = true;
    console.log('✅ SessionTracker: Session tracking initialized', {
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      sessionStart: this.sessionStart
    });
  }

  /**
   * Get or create a persistent visitor ID
   */
  getOrCreateVisitorId() {
    let visitorId = localStorage.getItem('visitorId');
    if (!visitorId) {
      // Generate a UUID-like visitor ID
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      localStorage.setItem('visitorId', visitorId);
      console.log('🆔 SessionTracker: Created new visitor ID:', visitorId);
    } else {
      console.log('🆔 SessionTracker: Loaded existing visitor ID:', visitorId);
    }
    return visitorId;
  }

  /**
   * Load existing session or create new one
   */
  loadOrCreateSession() {
    const storedSessionData = sessionStorage.getItem('sessionData');
    const now = new Date();
    
    if (storedSessionData) {
      try {
        const sessionData = JSON.parse(storedSessionData);
        const sessionAge = now.getTime() - new Date(sessionData.sessionStart).getTime();
        
        // Check if session is still valid (not expired)
        if (sessionAge < this.sessionTimeout) {
          this.sessionId = sessionData.sessionId;
          this.sessionStart = new Date(sessionData.sessionStart);
          this.lastActivityTime = new Date(sessionData.lastActivityTime || sessionData.sessionStart);
          console.log('🔄 SessionTracker: Resumed existing session:', this.sessionId);
          return;
        } else {
          console.log('⏰ SessionTracker: Session expired, creating new session');
        }
      } catch (error) {
        console.warn('⚠️ SessionTracker: Error loading session data:', error);
      }
    }
    
    // Create new session
    this.sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    this.sessionStart = now;
    this.lastActivityTime = now;
    this.sessionEnd = null;
    
    this.saveSessionData();
    console.log('🆕 SessionTracker: Created new session:', this.sessionId);
  }

  /**
   * Save session data to sessionStorage
   */
  saveSessionData() {
    const sessionData = {
      sessionId: this.sessionId,
      sessionStart: this.sessionStart.toISOString(),
      lastActivityTime: this.lastActivityTime.toISOString(),
      sessionEnd: this.sessionEnd ? this.sessionEnd.toISOString() : null
    };
    
    sessionStorage.setItem('sessionData', JSON.stringify(sessionData));
  }

  /**
   * Set up session end detection using various browser events
   */
  setupSessionEndDetection() {
    // Use beforeunload for immediate session end detection
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });

    // Use pagehide for mobile devices and back/forward cache
    window.addEventListener('pagehide', () => {
      this.endSession();
    });

    // Use visibilitychange to detect when tab becomes hidden
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        // End the session when tab becomes hidden (user likely leaving)
        console.log('👁️ SessionTracker: Tab hidden, ending session');
        this.endSession();
      } else if (document.visibilityState === 'visible') {
        // Tab became visible again - if session was ended, this indicates
        // the user came back to the same tab (not a new page load)
        console.log('👁️ SessionTracker: Tab visible again');
        this.updateLastActivity();
      }
    });

    console.log('👁️ SessionTracker: Session end detection events registered');
  }

  /**
   * Set up activity tracking
   */
  setupActivityTracking() {
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const throttledUpdateActivity = this.throttle(() => {
      this.updateLastActivity();
    }, 5000); // Throttle to once every 5 seconds
    
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity, { passive: true });
    });
    
    console.log('🏃 SessionTracker: Activity tracking events registered');
  }

  /**
   * Update last activity time
   */
  updateLastActivity() {
    this.lastActivityTime = new Date();
    this.saveSessionData();
  }

  /**
   * Start heartbeat to check for inactivity
   */
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      const now = new Date();
      const inactiveTime = now.getTime() - this.lastActivityTime.getTime();
      
      // If inactive for more than session timeout, end session
      if (inactiveTime > this.sessionTimeout) {
        console.log('💤 SessionTracker: Session timed out due to inactivity');
        this.endSession();
      }
    }, 60000); // Check every minute
    
    console.log('💓 SessionTracker: Heartbeat started');
  }

  /**
   * End the current session
   */
  endSession() {
    if (!this.sessionEnd) {
      this.sessionEnd = new Date();
      this.saveSessionData();
      console.log('🔚 SessionTracker: Session ended:', {
        sessionId: this.sessionId,
        visitorId: this.visitorId,
        sessionStart: this.sessionStart?.toISOString(),
        sessionEnd: this.sessionEnd?.toISOString(),
        duration: this.getSessionDuration(),
        lastActivityTime: this.lastActivityTime?.toISOString()
      });
      
      // Send final session data to backend if possible
      this.sendSessionEndToBackend();
    } else {
      console.warn('⚠️ SessionTracker: Session already ended, skipping duplicate end request');
    }
  }

  /**
   * Get current session data
   */
  getSessionData() {
    return {
      sessionId: this.sessionId,
      visitorId: this.visitorId,
      sessionStart: this.sessionStart,
      sessionEnd: this.sessionEnd,
      lastActivityTime: this.lastActivityTime
    };
  }

  /**
   * Get session duration in minutes
   */
  getSessionDuration() {
    if (!this.sessionStart) return 0;
    
    const endTime = this.sessionEnd || new Date();
    const durationMs = endTime.getTime() - this.sessionStart.getTime();
    return Math.max(0, Math.round(durationMs / (1000 * 60)));
  }

  /**
   * Check if session is currently active
   */
  isSessionActive() {
    return this.sessionId && !this.sessionEnd;
  }

  /**
   * Force end current session and start new one
   */
  resetSession() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    
    this.endSession();
    sessionStorage.removeItem('sessionData');
    
    // Reset session data
    this.sessionId = null;
    this.sessionStart = null;
    this.sessionEnd = null;
    this.lastActivityTime = null;
    
    // Reinitialize
    this.loadOrCreateSession();
    this.startHeartbeat();
    
    console.log('🔄 SessionTracker: Session reset completed');
  }

  /**
   * Cleanup method
   */
  destroy() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }
    this.endSession();
    console.log('🧹 SessionTracker: Cleanup completed');
  }

  /**
   * Send session end event to backend (best effort)
   */
  async sendSessionEndToBackend() {
    if (!this.sessionId || !this.sessionEnd) {
      console.warn('⚠️ SessionTracker: Cannot send session end - missing sessionId or sessionEnd', {
        sessionId: this.sessionId,
        sessionEnd: this.sessionEnd
      });
      return;
    }
    
    try {
      console.log('📤 SessionTracker: Sending session end event to backend...', {
        sessionId: this.sessionId,
        visitorId: this.visitorId,
        sessionStart: this.sessionStart?.toISOString(),
        sessionEnd: this.sessionEnd?.toISOString()
      });
      
      // Use sendBeacon for reliable delivery during page unload
      const sessionEndData = {
        sessionId: this.sessionId,
        visitorId: this.visitorId,
        sessionStart: this.sessionStart?.toISOString(),
        sessionEnd: this.sessionEnd?.toISOString(),
        action: 'session_end'
      };
      
      const payload = JSON.stringify(sessionEndData);
      
      // Try sendBeacon first (most reliable for page unload)
      if (navigator.sendBeacon) {
        console.log('🚀 SessionTracker: Attempting sendBeacon...');
        // Create a Blob with proper Content-Type for sendBeacon
        const blob = new Blob([payload], { type: 'application/json' });
        console.log('📄 SessionTracker: Blob created with payload:', payload);
        
        const success = navigator.sendBeacon(
          'https://trackable-gpt-backend.onrender.com/analytics/session-end',
          blob
        );
        
        console.log('📊 SessionTracker: sendBeacon result:', success);
        
        if (success) {
          console.log('✅ SessionTracker: Session end sent via sendBeacon');
          return;
        } else {
          console.warn('⚠️ SessionTracker: sendBeacon failed, trying fetch...');
        }
      } else {
        console.warn('⚠️ SessionTracker: sendBeacon not available, using fetch...');
      }
      
      // Fallback to fetch with keepalive
      try {
        const response = await fetch('https://trackable-gpt-backend.onrender.com/analytics/session-end', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true // Allows request to continue even if page is closing
        });
        
        if (response.ok) {
          console.log('✅ SessionTracker: Session end sent via fetch');
        } else {
          console.warn('⚠️ SessionTracker: Session end request failed:', response.status);
        }
      } catch (fetchError) {
        console.warn('⚠️ SessionTracker: Fetch failed for session end:', fetchError.message);
      }
      
    } catch (error) {
      // Don't throw errors during session end - just log
      console.warn('⚠️ SessionTracker: Failed to send session end to backend:', error.message);
    }
  }

  /**
   * Throttle function to limit frequent calls
   */
  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  }
}

// Create singleton instance
const sessionTracker = new SessionTracker();

export default sessionTracker;
